import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../../assets/styles/invoiceprint.css";
import { PRINT_TEXT } from "@utils/constants/print.constants";
import useGetOneConsignmentInvoice from "@hooks/consignment/useGetOneConsignmentInvoice";

const ConsignmentInvoicePrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { consignmentInvoiceData } = useGetOneConsignmentInvoice(id!);

  const retrieveValues = {
    invoice_date: consignmentInvoiceData?.invoice_date,
    SSIC: consignmentInvoiceData?.consignment_invoice_code,
    SSOC: consignmentInvoiceData?.consignment_code,
    grq_sale_order_no: consignmentInvoiceData?.reference_id,
    sales_person_name: consignmentInvoiceData?.sales_person_name,
    delivery_date: consignmentInvoiceData?.delivery_date,
    customer_name: consignmentInvoiceData?.customer_name,
    customer_phone: consignmentInvoiceData?.cus_phone,
    address: consignmentInvoiceData?.address,
    contact_name: consignmentInvoiceData?.contact_name,
    customer_address: consignmentInvoiceData?.customer_address,
    sub_total: consignmentInvoiceData?.sub_total,
    discount_amount: consignmentInvoiceData?.discount_amount,
    discount: consignmentInvoiceData?.discount,
    tax_amount: consignmentInvoiceData?.tax_amount,
    other_charges: consignmentInvoiceData?.other_charges,
    grand_total_amount: consignmentInvoiceData?.grand_total_amount,
    payment_type_name: consignmentInvoiceData?.payment_type_name,
    item_detail: consignmentInvoiceData?.products,
  };
  const chunkArray = (arr: any[], size: number) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };
  const chunkedItems = chunkArray(retrieveValues?.item_detail || [], 8);

  useEffect(() => {
    const handleAfterPrint = () => navigate(-1);
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [navigate]);

  useEffect(() => {
    setTimeout(() => window.print(), 300);
  }, []);


  return (
    <div className="bg-white print-h-auto min-h-screen font-sans text-gray-800 text-xs leading-normal print-page">
      <div className="mx-auto p-8 w-full max-w-4xl print-container">
        {/* Header Section */}
        <div className="header mb-4 text-left">
          <h1 className="text-xl font-bold mb-2 text-[18px]">STL</h1>
          <p className="text-sm mb-2">{PRINT_TEXT.invoice_voucher_text_one}</p>
          <p className="text-sm mb-2">{PRINT_TEXT.invoice_voucher_text_two}</p>
          <p className="text-sm mb-4">
            {PRINT_TEXT.invoice_voucher_text_three}
          </p>
        </div>

        <div className="border-b border-black mb-2 w-full"></div>
        <br />
        <div className="border-b border-black mb-1 w-full"></div>

        {/* Invoice Details */}
        <div className="invoice-details">
          <table className="details-table">
            <tbody>
              <tr>
                <td className="bill-to">
                  <p>
                    <strong>Bill To:</strong> {retrieveValues?.customer_name}
                  </p>
                  <p>{retrieveValues?.address}</p>
                  <p>Phone: {retrieveValues?.customer_phone}</p>
                  <p>Attention: {retrieveValues?.contact_name}</p>
                  <p>{retrieveValues?.customer_address}</p>
                </td>
                <td className="invoice-info mt-5">
                  <table>
                    <tbody>
                      <tr>
                        <td colSpan={1} className="border-remove"></td>
                        <td colSpan={2} className="border-remove text-center">
                          <h1 className="text-[30px]">Invoice</h1>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={1} className="border-remove"></td>
                        <td className="label">Invoice No</td>
                        <td className="label">{retrieveValues?.SSIC}</td>
                      </tr>
                      <tr>
                        <td colSpan={1} className="border-remove"></td>
                        <td className="label">Date</td>
                        <td className="label">
                          {retrieveValues?.invoice_date?.split("T")[0] ?? "-"}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={1} className="border-remove"></td>
                        <td className="label">Page</td>
                        <td className="label">1</td>
                      </tr>
                      <tr>
                        <td className="label text-center">C.O.NUMBER</td>
                        <td className="label text-left">SALESPERSON</td>
                        <td className="label text-center">DELIVERY DATE</td>
                      </tr>
                      <tr>
                        <td className="text-center border-bottom-remove">
                          {retrieveValues?.SSOC}
                        </td>
                        <td className="text-center border-bottom-remove">
                          {retrieveValues?.sales_person_name}
                        </td>
                        <td className="text-center border-bottom-remove">
                          {retrieveValues?.delivery_date?.split("T")[0] ?? "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Items Table */}
        <div className="invoice-body">
          <table className="items-table">
            <thead>
              <tr className="table-header">
                <th className="text-left">ITEM NO</th>
                <th className="text-left">DESCRIPTION</th>
                <th className="text-center">QTY</th>
                <th className="text-right">PRICE</th>
                <th className="text-right">DISCOUNT</th>
                <th className="text-center">UNIT</th>
                <th className="text-right">AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              {chunkedItems.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {group.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{item?.product_code}</td>
                      <td>{item?.description}</td>
                      <td className="text-center">{item?.qty}</td>
                      <td className="text-right">{item?.sales_price}</td>
                      <td>{item?.discount ? `${item.discount} %` : "0 %"}</td>
                      <td className="text-center">{item?.unit_name}</td>
                      <td className="text-right">{item?.amount}</td>
                    </tr>
                  ))}
                  {groupIndex < chunkedItems.length - 1 && (
                    <tr className="page-break-row">
                      <td colSpan={6} className="page-break"></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {/* Summary section, only show after last chunk */}
              <tr className="total-row">
                <td colSpan={2} className="border-remove text-left">
                  {retrieveValues?.payment_type_name}
                </td>
                <td colSpan={4} className="border-remove text-right">
                  {PRINT_TEXT.total_amount}
                </td>
                <td className="text-right">{retrieveValues?.sub_total}</td>
              </tr>
              <tr className="grand-total">
                <td colSpan={6} className="border-remove text-right">
                  {PRINT_TEXT.discount}
                </td>
                <td className="text-right">
                  {retrieveValues?.discount ? `${retrieveValues?.discount} %` : "0 %"}
                </td>
              </tr>
              <tr className="grand-total">
                <td colSpan={6} className="border-remove text-right">
                  {PRINT_TEXT.transport_charge}
                </td>
                <td className="text-right">
                  {retrieveValues?.discount_amount}
                </td>
              </tr>
              <tr className="total-row">
                <td colSpan={6} className="border-remove text-right">
                  {PRINT_TEXT.sale_tax}
                </td>
                <td className="text-right">{retrieveValues?.tax_amount}</td>
              </tr>
              <tr className="grand-total">
                <td colSpan={3} className="border-remove text-left">
                  {PRINT_TEXT.slae_invoice_note}
                </td>
                <td colSpan={3} className="border-remove text-right">
                  {PRINT_TEXT.less_deposit}
                </td>
                <td className="text-right">{retrieveValues?.other_charges}</td>
              </tr>
              <tr className="grand-total">
                <td colSpan={6} className="border-remove text-right">
                  {PRINT_TEXT.balance}
                </td>
                <td className="text-right">
                  {retrieveValues?.grand_total_amount}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Footer */}
        <div className="footer right-0 absolute">
          <div className="signature">
            <p>{PRINT_TEXT.authorized}</p>
            <div className="signature-line"></div>
          </div>
        </div>
        <div className="font-bold text-sm py-10 mx-5">YY-FO-SA-04 REV-00</div>

        {/* <div className="end-line border-b border-black w-full"></div> */}
      </div>
    </div>
  );
};

export default ConsignmentInvoicePrintPage;
